import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import * as fromUIStore from 'src/app/core/store';
import * as fromLocatorStore from 'src/app/locator-store';
import { updateLibraryDialogSaveClicked } from 'src/app/locator-store/actions/locator-library.actions';
import { LocatorLibrary } from 'src/app/locator/models/locator-library';
import { LocatorService } from 'src/app/locator/services/locator.service';
import { ProfilerLibrary } from 'src/app/profiler/model/profiler-library';
import { ProfilerService } from 'src/app/profiler/services/profiler.service';
import { DialogMode } from 'src/app/shared/atlas-dialog/enums/dialog-mode.enum';
import { DialogResult } from 'src/app/shared/atlas-dialog/enums/dialog-result.enum';
import { AddEditLibraryFormComponent } from '../add-edit-library-form/add-edit-library-form.component';

@Component({
  selector: 'atlas-edit-library-dialog',
  templateUrl: './edit-library-dialog.component.html',
  styleUrls: ['./edit-library-dialog.component.less'],
  standalone: false
})
export class EditLibraryDialogComponent {
  dialogMode: DialogMode;
  userLibraries: string[] = [];
  profilerLibraries: ProfilerLibrary[] = [];
  enableProfilerLibrarySelection = false;
  private isLocatorAllowed = false;
  private isProfilerAllowed = false;
  private subscription = new Subscription();

  constructor(
    private locatorService: LocatorService,
    private profilerService: ProfilerService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      entityName: string;
      headerPrefix: string;
      mode: DialogMode;
      library: LocatorLibrary;
      affirmativeButtonText: string;
    },
    private dialogRef: MatDialogRef<EditLibraryDialogComponent>,
    private store$: Store<fromLocatorStore.State>
  ) {}

  ngOnInit() {
    this.getAllLibraries();
    this.getAllProfilerLibraries();
    this.getProfilerLibrarySelectionAvailability();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private getAllLibraries() {
    this.subscription.add(
      this.locatorService
        .getAllLibraries()
        .subscribe((libraries: LocatorLibrary[]) => {
          this.userLibraries = libraries
            .filter((library) => this.data.library.name !== library.name)
            .map((library) => library.name);
        })
    );
  }

  private getAllProfilerLibraries() {
    this.subscription.add(
      this.profilerService
        .getAllProfilerLibraries()
        .subscribe((libraries: ProfilerLibrary[]) => {
          this.profilerLibraries = libraries;
        })
    );
  }

  private getProfilerLibrarySelectionAvailability() {
    this.subscription.add(
      this.store$
        .select(fromUIStore.isLocatorFeatureAllowed)
        .subscribe((isLocatorAllowed) => {
          this.isLocatorAllowed = !!isLocatorAllowed;
          this.updateProfilerLibrarySelectionAvailability();
        })
    );

    this.subscription.add(
      this.store$
        .select(fromUIStore.isProfilerFeatureAllowed)
        .subscribe((isProfilerAllowed) => {
          this.isProfilerAllowed = !!isProfilerAllowed;
          this.updateProfilerLibrarySelectionAvailability();
        })
    );
  }

  private updateProfilerLibrarySelectionAvailability() {
    this.enableProfilerLibrarySelection =
      this.isLocatorAllowed && this.isProfilerAllowed;
  }

  onSaveClicked(editLibraryFormComponent: AddEditLibraryFormComponent) {
    const newLibrary = editLibraryFormComponent.addEditLibraryForm.value;
    if (editLibraryFormComponent.addEditLibraryForm.valid) {
      this.store$.dispatch(
        updateLibraryDialogSaveClicked({
          id: newLibrary.id,
          libraryRequest: {
            name: newLibrary.libraryName,
            profilerLibraryId: this.enableProfilerLibrarySelection
              ? editLibraryFormComponent.getSelectedProfilerLibraryId()
              : null
          }
        })
      );
      this.dialogRef.close({
        action: DialogResult.Affirmative,
        data: editLibraryFormComponent.addEditLibraryForm.controls.libraryName
          .value
      });
    }
  }
}
